import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import { BackupCodesDialog } from "@/components/BackupCodesDialog";
import { ShieldCheck, ShieldAlert, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";

export default function SecurityPanel() {
  const { user, refresh } = useAuth();
  const [codes, setCodes] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match");
    if (pw.next.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const genCodes = async () => {
    try {
      const { data } = await api.post("/auth/2fa/backup-codes");
      setCodes(data.backup_codes);
    } catch (e) { toast.error(apiError(e)); }
  };

  const disable2fa = async () => {
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("Two-factor disabled");
      setDisableCode("");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const inputCls = "w-full bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none focus:border-[#C9A84A]/60 mb-3";

  return (
    <div className="space-y-5">
      {/* 2FA */}
      <div className="card-surface rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          {user.totp_enabled ? <ShieldCheck className="text-emerald-400" size={24} /> : <ShieldAlert className="text-[#FCD34D]" size={24} />}
          <div>
            <h3 className="font-serif font-bold text-lg text-white">Two-factor authentication</h3>
            <p className="text-slate-400 text-sm">
              {user.totp_enabled ? "Active — your account is protected." : "Add an authenticator app for extra security."}
            </p>
          </div>
        </div>

        {user.totp_enabled ? (
          <div className="space-y-4">
            <button onClick={genCodes} data-testid="generate-backup-codes"
              className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-full px-5 py-2.5 flex items-center gap-2">
              <KeyRound size={16} /> Generate backup codes
            </button>
            {user.role === "client" && (
              <div className="pt-4 border-t border-[#1E294B]">
                <p className="text-slate-400 text-sm mb-2">Disable 2FA (enter a current code):</p>
                <div className="flex gap-2 max-w-xs">
                  <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456"
                    className="flex-1 bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-3 py-2 text-white outline-none" />
                  <button onClick={disable2fa} className="text-red-300 border border-red-500/30 rounded-xl px-4 hover:bg-red-500/10">Disable</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <TwoFactorSetup onDone={refresh} />
        )}
      </div>

      {/* Change password */}
      <form onSubmit={changePw} className="card-surface rounded-2xl p-6" data-testid="security-change-password">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="text-[#C9A84A]" size={22} />
          <h3 className="font-serif font-bold text-lg text-white">Change password</h3>
        </div>
        <input type="password" placeholder="Current password" value={pw.current}
          onChange={(e) => setPw({ ...pw, current: e.target.value })} className={inputCls} required />
        <input type="password" placeholder="New password" value={pw.next}
          onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} required />
        <input type="password" placeholder="Confirm new password" value={pw.confirm}
          onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} required />
        <button disabled={busy} className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-full px-5 py-2.5 disabled:opacity-60">
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>

      {codes && <BackupCodesDialog codes={codes} open onClose={() => setCodes(null)} />}
    </div>
  );
}
